import React, { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Loader2, Filter } from "lucide-react";

const FOLDERS = ["general", "blog", "stories", "logos", "tools"];

export default function MediaLibrary() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [folder, setFolder] = useState("");
    const [mime, setMime] = useState("");
    const [folderUpload, setFolderUpload] = useState("general");
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const params = {};
            if (folder) params.folder = folder;
            if (mime) params.mime_type = mime;
            const { data } = await api.get("/admin/media", { params });
            setItems(data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [folder, mime]);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        fd.append("folder", folderUpload);
        setUploading(true);
        try {
            await api.post("/admin/media", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Yüklendi");
            await fetchItems();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Silinsin mi?")) return;
        try {
            await api.delete(`/admin/media/${id}`);
            toast.success("Silindi");
            setItems((p) => p.filter((it) => it.id !== id));
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-media-library">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <h1 className="text-2xl font-bold text-dark-navy">Medya Kütüphanesi</h1>
                <div className="flex items-center gap-2">
                    <select
                        value={folderUpload}
                        onChange={(e) => setFolderUpload(e.target.value)}
                        className="text-sm rounded-md border border-slate-300 px-2 py-2"
                        data-testid="media-upload-folder"
                    >
                        {FOLDERS.map((f) => (
                            <option key={f} value={f}>{f}</option>
                        ))}
                    </select>
                    <label
                        className="bg-dark-navy hover:bg-deep-blue text-white text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center gap-2 cursor-pointer"
                        data-testid="media-upload-btn"
                    >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        Dosya Yükle
                        <input
                            ref={inputRef}
                            type="file"
                            className="hidden"
                            onChange={handleUpload}
                            accept="image/*,application/pdf"
                        />
                    </label>
                </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-100 shadow-md p-4 flex flex-wrap items-center gap-3">
                <Filter className="w-4 h-4 text-deep-blue" />
                <select
                    value={folder}
                    onChange={(e) => setFolder(e.target.value)}
                    className="text-sm rounded-md border border-slate-300 px-2 py-2"
                    data-testid="media-filter-folder"
                >
                    <option value="">Tüm klasörler</option>
                    {FOLDERS.map((f) => (
                        <option key={f} value={f}>{f}</option>
                    ))}
                </select>
                <select
                    value={mime}
                    onChange={(e) => setMime(e.target.value)}
                    className="text-sm rounded-md border border-slate-300 px-2 py-2"
                    data-testid="media-filter-mime"
                >
                    <option value="">Tüm tipler</option>
                    <option value="image/jpeg">JPEG</option>
                    <option value="image/png">PNG</option>
                    <option value="image/webp">WebP</option>
                    <option value="application/pdf">PDF</option>
                </select>
                <span className="text-xs text-slate-500 ml-auto">{items.length} öğe</span>
            </div>

            {loading ? (
                <div className="text-sm text-slate-500">Yükleniyor...</div>
            ) : items.length === 0 ? (
                <div className="bg-white rounded-lg border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500" data-testid="media-empty">
                    Henüz medya yüklenmedi.
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="media-grid">
                    {items.map((it) => (
                        <div key={it.id} className="lift-card bg-white border border-slate-100 rounded-lg shadow-md hover:shadow-xl overflow-hidden">
                            <div className="aspect-square bg-light-bg flex items-center justify-center overflow-hidden">
                                {it.mime_type?.startsWith("image/") ? (
                                    <img
                                        src={it.url}
                                        alt={it.alt_text || it.original_name}
                                        className="object-cover w-full h-full"
                                    />
                                ) : (
                                    <span className="font-data text-xs text-slate-500">{it.mime_type}</span>
                                )}
                            </div>
                            <div className="p-3 text-xs">
                                <div className="font-semibold text-dark-navy truncate" title={it.original_name}>{it.original_name}</div>
                                <div className="text-slate-500 mt-0.5 flex justify-between">
                                    <span>{it.folder}</span>
                                    <span className="font-data">{Math.round((it.size || 0) / 1024)}KB</span>
                                </div>
                                <button
                                    onClick={() => handleDelete(it.id)}
                                    className="mt-2 inline-flex items-center gap-1 text-data-red hover:underline"
                                    data-testid={`media-delete-${it.id}`}
                                >
                                    <Trash2 className="w-3 h-3" /> Sil
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
